import { z } from 'zod';
/**
 * Товар — то, что агент продаёт в конкретном диалоге.
 *
 * Поля разбиты, а не свалены в один текст, по одной причине: их
 * подставляет в промпт не человек, а код. Один общий текст означал бы,
 * что «цена» существует только в голове того, кто его писал, и её
 * нельзя ни показать оператору в карточке диалога, ни проверить перед
 * запуском, ни сверить с лендингом.
 *
 * Заводится на воркспейс, а не на агента: один номер WhatsApp ведёт
 * несколько офферов, и товар определяется по первому сообщению клиента,
 * а не настройкой агента. Это возврат к устройству старой системы,
 * решение заказчицы от 23.09.2026.
 */
export const PRODUCT_NAME_MAX = 200;
export const PRODUCT_TEXT_MAX = 2_000;
export const productSchema = z.object({
    id: z.uuid(),
    /** Название ровно как на лендинге: по нему идёт сопоставление. */
    name: z.string(),
    /**
     * Другие написания того же товара. Виджет подставляет название из
     * настроек лендинга, и оно расходится с каталогом в мелочах —
     * регистр, дефис, добавленное слово. Список синонимов дешевле,
     * чем догадки на стороне модели.
     */
    aliases: z.array(z.string()),
    /**
     * Группа оффера из номера заявки: `[subid-offer_group_id]`. Если
     * заполнена, сопоставление идёт по ней, а не по названию — цифры
     * не переписывают, а названия меняют на ходу.
     */
    offerGroupId: z.string().nullable(),
    /** Одна строка: что это и для чего. */
    summary: z.string(),
    /** Состав с упаковки. Пусто — агенту нечего ответить на вопрос о составе. */
    composition: z.string(),
    /** Два-три плюса простыми словами, без медицинских обещаний. */
    benefits: z.array(z.string()),
    /** Цена как её называют клиенту: «Q269, прежняя Q538». */
    price: z.string(),
    /** Сроки и города. Пусто — агент передаёт вопрос в колл-центр. */
    delivery: z.string(),
    /** Всё, что не влезло: акция, гарантия, особенности приёма. */
    note: z.string(),
    /** Выключенный товар не подставляется и не сопоставляется. */
    isActive: z.boolean(),
    /**
     * Откуда товар. `null` — завели у нас руками; строка — имя внешнего
     * сервиса, из которого он приезжает.
     *
     * На экране это разница между «правьте здесь» и «правьте там»:
     * пришедший извне товар у нас только для чтения, потому что правка
     * иначе молча исчезнет при следующей синхронизации.
     */
    source: z.string().nullable(),
    /** Когда синхронизация последний раз видела этот товар в источнике. */
    syncedAt: z.iso.datetime().nullable(),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
});
const text = (max) => z.string().trim().max(max);
export const createProductInputSchema = z.object({
    name: z.string().trim().min(1).max(PRODUCT_NAME_MAX),
    aliases: z.array(z.string().trim().min(1).max(PRODUCT_NAME_MAX)).max(20).default([]),
    offerGroupId: text(64).default(''),
    summary: text(PRODUCT_TEXT_MAX).default(''),
    composition: text(PRODUCT_TEXT_MAX).default(''),
    benefits: z.array(z.string().trim().min(1).max(300)).max(10).default([]),
    price: text(300).default(''),
    delivery: text(PRODUCT_TEXT_MAX).default(''),
    note: text(PRODUCT_TEXT_MAX).default(''),
    isActive: z.boolean().default(true),
});
export const updateProductInputSchema = createProductInputSchema.partial();
/**
 * Что из первого сообщения удалось разобрать.
 *
 * Виджет на лендинге присылает шаблон:
 * «Номер заявки 12345-678. Здравствуйте! Интересует Urodoc. Хочу
 * воспользоваться бесплатной консультацией и узнать подробнее.»
 *
 * Отсюда берётся сразу три вещи: источник трафика для конверсии в
 * Keitaro, группа оффера и название товара. До 23.09.2026 мы считали,
 * что источник придётся доставать из OrderHub.
 */
export const firstMessageMarksSchema = z.object({
    subid: z.string().nullable(),
    offerGroupId: z.string().nullable(),
    productName: z.string().nullable(),
});
//# sourceMappingURL=products.js.map